import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Gusto, PuntoVendita, StockCumulativoItem } from '../types';
import ApiService from '../services/api';
import { Archive, Calendar, MapPin, Send, Info } from 'lucide-react';
import moment from 'moment';
import { PageContainer, PageHeader, Textarea, Input, Select, Alert, Spinner, LoadingState } from '../components/ui';
import {
  SectionCard,
  FormSection,
  InputGroup,
  FieldLabel,
  GustiGrid,
  GustoCard,
  GustoHeader,
  GustoName,
  GustoColor,
  StockInfo,
  StockRow,
  QuantityControl,
  QuantityButton,
  QuantityButtonWide,
  QuantityInput,
  SummaryCard,
  SummaryTitle,
  SummaryValue,
  SummaryUnit,
  SubmitButton,
  CenteredActions,
  InfoBanner
} from '../components/rilevazione/ui';

// ============================================================
// COMPONENTE PRINCIPALE
// ============================================================
interface GiacenzaPageProps {
  onNavigate?: (page: string) => void;
}

const GiacenzaPage: React.FC<GiacenzaPageProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [data, setData] = useState(moment().format('YYYY-MM-DD'));
  const [puntoVenditaId, setPuntoVenditaId] = useState<number | null>(null);
  const [gusti, setGusti] = useState<Gusto[]>([]);
  const [puntiVendita, setPuntiVendita] = useState<PuntoVendita[]>([]);
  const [quantita, setQuantita] = useState<{ [key: number]: number }>({});
  const [stockCumulativo, setStockCumulativo] = useState<StockCumulativoItem[]>([]);
  const [note, setNote] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const gustiVisibili: Gusto[] = [
    ...gusti,
    ...stockCumulativo
      .filter(stock => !gusti.some(gusto => gusto.id === stock.gusto_id))
      .map(stock => ({
        id: stock.gusto_id,
        nome: stock.gusto_nome,
        categoria: stock.gusto_categoria,
        colore: stock.gusto_colore,
        attivo: false,
        created_at: ''
      }))
  ].sort((a, b) => a.nome.localeCompare(b.nome));

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caricamento una tantum al mount
  }, []);

  useEffect(() => {
    if (data && puntoVenditaId) {
      loadStockCumulativo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ricarica solo al cambio di data/punto vendita; loadStockCumulativo è ricreata ad ogni render
  }, [data, puntoVenditaId]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError('');

      const [gustiData, puntiVenditaData] = await Promise.all([
        ApiService.getGusti(),
        ApiService.getPuntiVendita()
      ]);

      setGusti(gustiData.filter(g => g.attivo));
      setPuntiVendita(puntiVenditaData);

      if (user?.punto_vendita_id) {
        setPuntoVenditaId(user.punto_vendita_id);
      } else if (puntiVenditaData.length === 1) {
        setPuntoVenditaId(puntiVenditaData[0].id);
      }
    } catch (error) {
      console.error('Errore caricamento dati:', error);
      setError('Errore durante il caricamento dei dati');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStockCumulativo = async () => {
    if (!data || !puntoVenditaId) return;

    try {
      const response = await ApiService.getStockCumulativo({ data, puntoVenditaId });
      setStockCumulativo(response.stock);

      // Pre-compila le quantità con lo stock cumulativo
      const newQuantita: { [key: number]: number } = {};
      response.stock.forEach(item => {
        newQuantita[item.gusto_id] = parseFloat(item.stock_totale.toFixed(2));
      });
      setQuantita(newQuantita);
    } catch (error: any) {
      console.error('Errore caricamento stock cumulativo:', error);
      // Se è il primo giorno, non c'è stock precedente - non è un errore
      if (!error.response || error.response.status !== 404) {
        setError('Errore caricamento stock cumulativo');
      }
    }
  };

  const incrementQuantita = (gustoId: number, delta: number) => {
    setQuantita(prev => {
      const current = prev[gustoId] || 0;
      return { ...prev, [gustoId]: Math.max(0, current + delta) };
    });
  };

  const resetQuantita = (gustoId: number) => {
    setQuantita(prev => ({ ...prev, [gustoId]: 0 }));
  };

  const getTotaleKg = () => {
    return Object.values(quantita).reduce((total, qty) => total + qty, 0);
  };

  const getGustiSelezionati = () => {
    return Object.entries(quantita)
      .filter(([_, qty]) => qty > 0)
      .map(([gustoId, qty]) => ({
        gusto_id: parseInt(gustoId),
        quantita_kg: qty
      }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!puntoVenditaId) {
      setError('Seleziona un punto vendita');
      return;
    }

    const gustiSelezionati = getGustiSelezionati();

    try {
      setIsSubmitting(true);
      const response = await ApiService.saveGiacenza({
        data,
        puntoVenditaId,
        gusti: gustiSelezionati,
        note
      });

      setSuccess(`Giacenza salvata. Totale: ${response.kg_totale.toFixed(2)} kg`);

      // Ricarica i dati dopo 2 secondi
      setTimeout(() => {
        loadStockCumulativo();
      }, 2000);
    } catch (error: any) {
      console.error('Errore salvataggio giacenza:', error);
      setError(error.response?.data?.error || 'Errore durante il salvataggio');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingState />
      </PageContainer>
    );
  }

  const totaleKg = getTotaleKg();
  const numGusti = getGustiSelezionati().length;

  return (
    <PageContainer>
      <PageHeader title="Giacenza Serale" icon={<Archive />} />

      <InfoBanner>
        <Info size={24} />
        <div>
          <strong>Registra le rimanenze di fine giornata.</strong>
          <br />
          I valori pre-compilati mostrano lo stock teorico (giacenza ieri + produzione oggi - smaltimenti).
          Aggiusta i valori in base alla realtà del gelato rimasto.
        </div>
      </InfoBanner>

      {error && <Alert $variant="error">{error}</Alert>}
      {success && <Alert $variant="success">{success}</Alert>}

      <form onSubmit={handleSubmit}>
        <SectionCard>
          <FormSection>
            <InputGroup>
              <FieldLabel>
                <Calendar size={20} />
                Data
              </FieldLabel>
              <Input
                type="date"
                value={data}
                onChange={(e) => setData(e.target.value)}
                required
              />
            </InputGroup>

            <InputGroup>
              <FieldLabel>
                <MapPin size={20} />
                Punto Vendita
              </FieldLabel>
              <Select
                value={puntoVenditaId || ''}
                onChange={(e) => setPuntoVenditaId(parseInt(e.target.value))}
                required
              >
                <option value="">Seleziona punto vendita</option>
                {puntiVendita.map(pv => (
                  <option key={pv.id} value={pv.id}>{pv.nome}</option>
                ))}
              </Select>
            </InputGroup>
          </FormSection>
        </SectionCard>

        <SectionCard>
          <h2 style={{ marginTop: 0, marginBottom: '1.5rem' }}>Gusti Disponibili</h2>
          <GustiGrid>
            {gustiVisibili.map(gusto => {
              const stock = stockCumulativo.find(s => s.gusto_id === gusto.id);
              const qty = quantita[gusto.id] || 0;
              const isSelected = qty > 0;

              return (
                <GustoCard key={gusto.id} className={isSelected ? 'selected' : ''}>
                  <GustoHeader>
                    <GustoName>
                      <GustoColor color={gusto.colore} />
                      {gusto.nome}{!gusto.attivo ? ' (disattivato)' : ''}
                    </GustoName>
                  </GustoHeader>

                  {stock && (
                    <StockInfo>
                      <StockRow>
                        <span>Giacenza ieri:</span>
                        <span>{stock.giacenza_precedente.toFixed(2)} kg</span>
                      </StockRow>
                      <StockRow>
                        <span>Produzione oggi:</span>
                        <span>{stock.produzione_oggi.toFixed(2)} kg</span>
                      </StockRow>
                      <StockRow>
                        <span>Smaltimento:</span>
                        <span>-{stock.smaltimento_oggi.toFixed(2)} kg</span>
                      </StockRow>
                      <StockRow>
                        <span>Stock teorico:</span>
                        <span>{stock.stock_totale.toFixed(2)} kg</span>
                      </StockRow>
                    </StockInfo>
                  )}

                  <QuantityControl>
                    <QuantityButton
                      type="button"
                      onClick={() => incrementQuantita(gusto.id, -1)}
                      disabled={qty === 0}
                      title="-1 kg"
                    >
                      -1
                    </QuantityButton>
                    <QuantityButton
                      type="button"
                      onClick={() => incrementQuantita(gusto.id, -0.1)}
                      disabled={qty === 0}
                      title="-0,1 kg"
                    >
                      -0.1
                    </QuantityButton>
                    <QuantityInput
                      type="number"
                      step="0.1"
                      min="0"
                      value={qty || ''}
                      placeholder="0"
                      onChange={(e) => {
                        const val = e.target.value === '' ? 0 : Math.max(0, parseFloat(e.target.value) || 0);
                        setQuantita(prev => ({ ...prev, [gusto.id]: val }));
                      }}
                    />
                    <QuantityButtonWide
                      type="button"
                      onClick={() => resetQuantita(gusto.id)}
                      disabled={qty === 0}
                    >
                      Finito
                    </QuantityButtonWide>
                  </QuantityControl>
                </GustoCard>
              );
            })}
          </GustiGrid>
        </SectionCard>

        <SectionCard>
          <InputGroup>
            <FieldLabel>Note (opzionali)</FieldLabel>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Es: Manca cioccolato fondente, aggiungere alle rimanenze di domani..."
              maxLength={500}
            />
          </InputGroup>
        </SectionCard>

        <SummaryCard>
          <SummaryTitle>Totale Giacenza</SummaryTitle>
          <SummaryValue>
            {totaleKg.toFixed(2)} <SummaryUnit>kg</SummaryUnit>
          </SummaryValue>
          <div>{numGusti} gusti selezionati</div>
        </SummaryCard>

        <CenteredActions>
          <SubmitButton type="submit" $tone="success" disabled={isSubmitting || numGusti === 0}>
            {isSubmitting ? (
              <>
                <Spinner $size={20} />
                Salvataggio...
              </>
            ) : (
              <>
                <Send size={20} />
                Salva Giacenza
              </>
            )}
          </SubmitButton>
        </CenteredActions>
      </form>
    </PageContainer>
  );
};

export default GiacenzaPage;
